<template>
  <div class="login-page">
    <div class="login-container">
      <!-- 左侧装饰区域 -->
      <div class="decoration-area">
        <!-- Logo区域 - 移到最上方 -->
        <div class="logo-container">
          <img src="@/assets/plant/logo.png" alt="芽丘" class="logo">
          <h1 class="brand-name">芽丘</h1>
        </div>
        
        <!-- 欢迎文字 - 移到Logo下方 -->
        <div class="welcome-text">
          <h2>欢迎来到芽丘创造空间</h2>
          <p>让我们一起开启成长之旅吧！</p>
        </div>
        
        <!-- Scratch小猫 - 移到底部，作为装饰 -->
        <div
          class="scratch-cat-container"
          ref="scratchCatContainer"
          :style="catContainerStyle"
        >
          <svg class="scratch-cat" :style="catStyle" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <!-- 小猫尾巴（在身体后面，紫色尾尖） -->
            <path d="M 50 150 Q 45 130 55 120 Q 60 110 65 115" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <ellipse cx="58" cy="118" rx="6" ry="8" fill="#7E57C2"/>

            <!-- 小猫身体：圆滚滚的小身子 -->
            <ellipse cx="100" cy="145" rx="26" ry="28" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <!-- 肚皮（白色） -->
            <ellipse cx="100" cy="148" rx="18" ry="20" fill="#FFF8E1" stroke="#FFE0B2" stroke-width="1.5"/>

            <!-- 双腿（小短腿） -->
            <ellipse cx="92" cy="165" rx="8" ry="10" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <ellipse cx="108" cy="165" rx="8" ry="10" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <!-- 脚掌 -->
            <ellipse cx="92" cy="175" rx="8" ry="5" fill="#FFA726"/>
            <ellipse cx="108" cy="175" rx="8" ry="5" fill="#FFA726"/>

            <!-- 左右手臂（常态：抱信封） -->
            <g :style="normalArmStyle">
              <!-- 左臂（弯曲，抱信封） -->
              <path d="M 75 135 Q 70 125 75 115" fill="none" stroke="#FFA726" stroke-width="8" stroke-linecap="round"/>
              <ellipse cx="78" cy="118" rx="6" ry="8" fill="#FFA726" stroke="#FB8C00" stroke-width="1.5"/>
              <!-- 右臂（弯曲，抱信封） -->
              <path d="M 125 135 Q 130 125 125 115" fill="none" stroke="#FFA726" stroke-width="8" stroke-linecap="round"/>
              <ellipse cx="122" cy="118" rx="6" ry="8" fill="#FFA726" stroke="#FB8C00" stroke-width="1.5"/>
            </g>

            <!-- 信封（白色，在身体前） -->
            <g :style="normalArmStyle">
              <rect x="82" y="120" width="36" height="24" rx="3" fill="#FFFFFF" stroke="#E0E0E0" stroke-width="1.5"/>
              <path d="M 82 120 Q 100 128 118 120" fill="#F5F5F5"/>
            </g>

            <!-- 小猫头部（大圆形） -->
            <circle cx="100" cy="80" r="58" fill="#FFA726" stroke="#FB8C00" stroke-width="2"
                    :transform="`rotate(${headRotation} 100 80)`"/>

            <!-- 额头花纹（两条弯曲的橙色条纹，在眼睛上方） -->
            <path d="M 75 50 Q 85 48 95 52" stroke="#FB8C00" stroke-width="4" fill="none" stroke-linecap="round"/>
            <path d="M 105 52 Q 115 48 125 50" stroke="#FB8C00" stroke-width="4" fill="none" stroke-linecap="round"/>

            <!-- 左耳（圆润的三角形，紫色内耳） -->
            <path d="M 65 50 Q 75 20 90 45 Q 85 50 75 55 Z" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <path d="M 70 48 Q 78 28 88 48 Q 82 52 75 52 Z" fill="#7E57C2"/>

            <!-- 右耳（圆润的三角形，紫色内耳） -->
            <path d="M 110 45 Q 125 20 135 50 Q 125 55 115 50 Z" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            <path d="M 112 48 Q 122 28 130 48 Q 125 52 118 52 Z" fill="#7E57C2"/>

            <!-- 脸部白色区域（大椭圆形，覆盖眼睛、鼻子、嘴巴） -->
            <ellipse cx="100" cy="95" rx="42" ry="35" fill="#FFF8E1" stroke="#FFE0B2" stroke-width="1.5"/>

            <!-- 大眼睛：超大圆形黑色，白色高光在左上角 -->
            <!-- 左眼（固定位置） -->
            <circle cx="88" cy="85" r="18" fill="#212121"/>
            <circle cx="82" cy="78" r="8" fill="#FFFFFF"/>
            <!-- 瞳孔跟随鼠标（在眼睛内部移动） -->
            <circle :cx="88 + eyeOffsetX * 0.5" :cy="85 + eyeOffsetY * 0.5" r="8" fill="#000000" opacity="0.8"/>
            
            <!-- 右眼（固定位置） -->
            <circle cx="112" cy="85" r="18" fill="#212121"/>
            <circle cx="106" cy="78" r="8" fill="#FFFFFF"/>
            <!-- 瞳孔跟随鼠标（在眼睛内部移动） -->
            <circle :cx="112 + eyeOffsetX * 0.5" :cy="85 + eyeOffsetY * 0.5" r="8" fill="#000000" opacity="0.8"/>

            <!-- 鼻子（小椭圆形，深粉色，在白色脸部区域的上边缘） -->
            <ellipse cx="100" cy="95" rx="5" ry="6" fill="#D84315"/>

            <!-- 嘴巴（简单的向上弯曲的黑色线条） -->
            <path d="M 100 102 Q 95 108 100 110" stroke="#212121" stroke-width="2" fill="none" stroke-linecap="round"/>

            <!-- 胡须（从白色脸部区域两侧延伸，每边3根，稍微向上弯曲） -->
            <line x1="58" y1="92" x2="75" y2="90" stroke="#212121" stroke-width="2" stroke-linecap="round"/>
            <line x1="58" y1="98" x2="75" y2="97" stroke="#212121" stroke-width="2" stroke-linecap="round"/>
            <line x1="58" y1="104" x2="75" y2="104" stroke="#212121" stroke-width="2" stroke-linecap="round"/>
            <line x1="142" y1="92" x2="125" y2="90" stroke="#212121" stroke-width="2" stroke-linecap="round"/>
            <line x1="142" y1="98" x2="125" y2="97" stroke="#212121" stroke-width="2" stroke-linecap="round"/>
            <line x1="142" y1="104" x2="125" y2="104" stroke="#212121" stroke-width="2" stroke-linecap="round"/>

            <!-- 左手（捂眼时显示，上抬的手臂） -->
            <g class="hand-left" :style="handLeftStyle" :class="{ 'hand-animate': isCoveringEyes }">
              <path d="M 75 135 Q 65 110 75 85 Q 80 75 85 80" fill="none" stroke="#FFA726" stroke-width="10" stroke-linecap="round"/>
              <ellipse cx="88" cy="78" rx="12" ry="15" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            </g>

            <!-- 右手（捂眼时显示，上抬的手臂） -->
            <g class="hand-right" :style="handRightStyle" :class="{ 'hand-animate': isCoveringEyes }">
              <path d="M 125 135 Q 135 110 125 85 Q 120 75 115 80" fill="none" stroke="#FFA726" stroke-width="10" stroke-linecap="round"/>
              <ellipse cx="112" cy="78" rx="12" ry="15" fill="#FFA726" stroke="#FB8C00" stroke-width="2"/>
            </g>
          </svg>
        </div>
        
       
      </div>

      <!-- 右侧登录区域 -->
      <div class="login-area">
        <div class="login-form-container">
          <h2 class="login-title">登录</h2>
          <el-form 
            :model="ruleForm" 
            ref="ruleForm" 
            :rules="rules" 
            class="login-form"
          >
            <el-form-item prop="userAccount">
              <el-input 
                v-model="ruleForm.userAccount" 
                placeholder="请输入账号"
                prefix-icon="el-icon-user"
              ></el-input>
            </el-form-item>
            <el-form-item prop="password">
              <el-input 
                v-model="ruleForm.password" 
                type="password" 
                placeholder="请输入密码"
                prefix-icon="el-icon-lock"
                @keyup.enter.native="login(ruleForm)"
                @focus="handlePasswordFocus"
                @blur="handlePasswordBlur"
                @input="handlePasswordInput"
              ></el-input>
            </el-form-item>
            <el-form-item>
              <el-button 
                type="primary" 
                class="login-btn" 
                :loading="logining"
                @click.native.prevent="login(ruleForm)"
              >
                {{ logining ? '登录中...' : '登 录' }}
              </el-button>
            </el-form-item>
            <p class="login-compliance-tip">
              本平台面向公众提供科普与实践内容，不限定未成年人使用。
            </p>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      fullscreenLoading: '',
      loginUrl: "user/login",
      ruleForm: {
        userAccount: "",
        password: ""
      },
      rules: {
        userAccount: [{
          required: true,
          message: "请输入账号",
          trigger: "blur"
        }],
        password: [{
          required: true,
          message: "请输入密码",
          trigger: "blur"
        }]
      },
      logining: false,
      greenhouseId: "",
      // 小猫动画相关
      mouseX: 0,
      mouseY: 0,
      headRotation: 0,
      eyeOffsetX: 0,
      eyeOffsetY: 0,
      isCoveringEyes: false,
      catOffsetX: 0,
      catOffsetY: 0,
      catCenterX: 0,
      catCenterY: 0,
    };
  },
  computed: {
    catContainerStyle() {
      return {
        transform: `translateY(-50%) translate(${this.catOffsetX}px, ${this.catOffsetY}px)`,
        transition: 'transform 0.15s ease-out',
      };
    },
    catStyle() {
      return {
        transform: `rotate(${this.headRotation}deg)`,
        transformOrigin: '100px 80px',
        transition: 'transform 0.1s ease-out'
      };
    },
    // 常态手臂样式：捂眼时渐隐
    normalArmStyle() {
      return {
        opacity: this.isCoveringEyes ? 0 : 1,
        transition: 'opacity 0.2s ease-out'
      };
    },
    handLeftStyle() {
      return {
        opacity: this.isCoveringEyes ? 1 : 0,
        transition: 'opacity 0.3s ease-out'
      };
    },
    handRightStyle() {
      return {
        opacity: this.isCoveringEyes ? 1 : 0,
        transition: 'opacity 0.3s ease-out'
      };
    }
  },
  created() {
    window.localStorage.removeItem("ms_username");
  },
  mounted() {
    // 监听全局鼠标移动
    window.addEventListener('mousemove', this.handleGlobalMouseMove);
    // 计算小猫初始位置
    this.updateCatCenter();
    window.addEventListener('resize', this.updateCatCenter);
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.handleGlobalMouseMove);
    window.removeEventListener('resize', this.updateCatCenter);
  },
  methods: {
    updateCatCenter() {
      this.$nextTick(() => {
        const el = this.$refs.scratchCatContainer;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        this.catCenterX = rect.left + rect.width / 2;
        this.catCenterY = rect.top + rect.height / 2;
      });
    },
    updateCatPose(mouseX, mouseY) {
      if (!this.catCenterX || !this.catCenterY) {
        this.updateCatCenter();
        return;
      }

      const deltaX = mouseX - this.catCenterX;
      const deltaY = mouseY - this.catCenterY;

      // 计算头部旋转角度（限制在-25到25度之间）
      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
      this.headRotation = Math.max(-25, Math.min(25, angle - 90));

      // 计算眼球偏移（限制眼球移动范围在眼白内，眼睛半径18，所以最大移动约10）
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const eyeMaxOffset = 8; // 限制眼球移动范围（眼睛半径18，瞳孔半径8，所以最大移动约10）
      const normalizedDistance = Math.min(150, distance);
      if (distance > 0) {
        const eyeFactor = Math.min(eyeMaxOffset, normalizedDistance / 20);
        this.eyeOffsetX = (deltaX / distance) * eyeFactor;
        this.eyeOffsetY = (deltaY / distance) * eyeFactor;
      } else {
        this.eyeOffsetX = 0;
        this.eyeOffsetY = 0;
      }

      // 身体轻微跟随鼠标移动（整体位移）
      const bodyMaxOffset = 15;
      if (distance > 0) {
        const bodyFactor = Math.min(1, distance / 300);
        this.catOffsetX = (deltaX / distance) * bodyMaxOffset * bodyFactor;
        this.catOffsetY = (deltaY / distance) * bodyMaxOffset * bodyFactor;
      } else {
        this.catOffsetX = 0;
        this.catOffsetY = 0;
      }
    },
    handleGlobalMouseMove(event) {
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      this.updateCatPose(this.mouseX, this.mouseY);
    },
    handlePasswordFocus() {
      this.isCoveringEyes = true;
    },
    handlePasswordBlur() {
      // 延迟一下再移开手，让用户看到效果
      setTimeout(() => {
        this.isCoveringEyes = false;
      }, 300);
    },
    handlePasswordInput() {
      // 输入时保持捂眼状态
      this.isCoveringEyes = true;
    },
    login(ruleForm) {
      this.logining = true;
      this.$axios
        .post(this.$robotApi.login, JSON.stringify(ruleForm))
        .then(res => {
          if (res.data.errorCode == 0) {
            this.logining = false;
            this.$message.success("登录成功！");
            localStorage.setItem("ms_username", res.data.data.userName);
            localStorage.setItem("ms_userId", res.data.data.userId);
            localStorage.setItem("ms_lastLoginTime", res.data.data.lastLoginTime);
            this.fullscreenLoading = true;
            this.getGreenhouseId(res.data.data.userId);
            setTimeout(() => {
              this.fullscreenLoading = false;
              this.$router.push("/cockpit1");
            }, 1000);
          } else {
            this.$message.error("登录失败，用户名或密码错误");
            this.logining = false;
          }
        })
        .catch(error => {
          this.$message.error("登录失败，请检查网络！", error);
          this.logining = false;
        });
    },
    getGreenhouseId(uid) {
      this.$request
        .callApi("GET", this.$robotApi.getGreenhouseByUser, {userId:uid})
        .then((res) => {
          if(res.data) {
            this.greenhouseId = res.data.greenhouseId;
            localStorage.setItem("ms_greenhouseId", res.data.greenhouseId);
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  /* 童趣场景背景：上方天空，下方草地 */
  background: linear-gradient(
    to bottom,
    #8fd3f4 0%,
    #a6e6ff 45%,
    #c9f0c5 46%,
    #a3e07b 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 12%;
    bottom: 32%;
    /* 远处山脉 */
    background: linear-gradient(to bottom, #8d9ba3 0%, #76848d 100%);
    opacity: 0.9;
    clip-path: polygon(
      0 100%,
      12% 45%,
      30% 70%,
      50% 38%,
      72% 68%,
      100% 40%,
      100% 100%
    );
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: radial-gradient(
      circle at 30% 30%,
      #fff9c4 0%,
      #ffe082 45%,
      rgba(255, 224, 130, 0) 70%
    );
    top: 8%;
    right: 14%;
    opacity: 0.9;
    z-index: 0;
  }
}

.login-container {
  width: 1000px;
  height: 520px;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.decoration-area {
  flex: 1.1;
  background: transparent;
  padding: 32px 30px 40px;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px; // 添加间距
}

.logo-container {
  text-align: left;
  color: #ffffff;
  position: relative;
  z-index: 5; // 提高层级，低于小猫但高于装饰元素
  
  .logo {
    width: 80px;  // 缩小logo尺寸
    height: 80px;
    margin-bottom: 10px;
    animation: bounce 2s infinite;
    filter: drop-shadow(0 5px 15px rgba(0,0,0,0.2));
  }

  .brand-name {
    font-size: 28px;
    font-weight: bold;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    letter-spacing: 2px;
  }
}

.welcome-text {
  color: #fff;
  text-align: left;
  position: relative;
  z-index: 5; // 提高层级

  h2 {
    font-size: 24px;
    margin: 0 0 6px;
    font-weight: 600;
    letter-spacing: 1px;
  }

  p {
    font-size: 15px;
    margin: 0;
    opacity: 0.9;
    letter-spacing: 0.5px;
  }
}

.scratch-cat-container {
  position: absolute; // 保持绝对定位
  left: 50%;
  bottom: 20px; // 移到底部
  transform: translateX(-50%); // 水平居中
  width: 180px; // 适当缩小
  height: 180px;
  z-index: 10;
  pointer-events: none;
}

.scratch-cat {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 5px 15px rgba(0,0,0,0.3));
  
  .hand-left,
  .hand-right {
    pointer-events: none;
    transform-origin: center;
    
    &.hand-animate {
      animation: coverEyes 0.3s ease-out;
    }
  }
}

@keyframes coverEyes {
  from {
    transform: translateY(10px) scale(0.8);
  }
  to {
    transform: translateY(0) scale(1);
  }
}

.logo-container {
  text-align: left;
  color: #ffffff;
  position: relative;
  z-index: 2;
  margin-left: 8px;
  
  .logo {
    width: 120px;
    height: 120px;
    margin-bottom: 20px;
    animation: bounce 2s infinite;
    filter: drop-shadow(0 5px 15px rgba(0,0,0,0.2));
  }

  .brand-name {
    font-size: 36px;
    font-weight: bold;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    letter-spacing: 2px;
  }
}

.decoration-elements {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  animation: float 6s infinite;
  backdrop-filter: blur(5px);

  &.element1 {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    top: 20%;
    left: 20%;
    animation-delay: 0s;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }

  &.element2 {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.3);
    top: 40%;
    right: 20%;
    animation-delay: 1s;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }

  &.element3 {
    width: 80px;
    height: 80px;
    background: rgba(255, 255, 255, 0.15);
    bottom: 20%;
    left: 30%;
    animation-delay: 2s;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }

  &.element4 {
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.25);
    bottom: 30%;
    right: 30%;
    animation-delay: 3s;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
}

.welcome-text {
  color: #fff;
  text-align: left;
  position: relative;
  z-index: 2;
  margin-top: 4px;
  margin-left: 8px;

  h2 {
    font-size: 28px;
    margin: 0 0 6px;
    font-weight: 600;
    letter-spacing: 1px;
  }

  p {
    font-size: 16px;
    margin: 0;
    opacity: 0.9;
    letter-spacing: 0.5px;
  }
}

.login-area {
  flex: 0 0 420px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 40px 30px;
  background: transparent;
}

.login-form-container {
  width: 100%;
  max-width: 380px;
  animation: fadeIn 0.5s ease-out;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 24px;
  padding: 32px 36px 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.login-title {
  text-align: center;
  color: #37474f;
  font-size: 28px;
  margin-bottom: 30px;
  font-weight: 600;
  letter-spacing: 1px;
}

.login-form {
  .el-input {
    margin-bottom: 20px;
    
    ::v-deep .el-input__inner {
      height: 50px;
      line-height: 50px;
      border-radius: 25px;
      padding-left: 45px;
      font-size: 16px;
      border: 2px solid #eee;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.9);

      &:focus {
        border-color: #6B73FF;
        box-shadow: 0 0 0 3px rgba(107, 115, 255, 0.1);
        background: #fff;
      }

      &:hover {
        border-color: #6B73FF;
      }
    }

    ::v-deep .el-input__prefix {
      left: 15px;
      color: #6B73FF;
      font-size: 18px;
    }
  }

  .login-btn {
    width: 60%;
    height: 50px;
    border-radius: 25px;
    font-size: 16px;
    background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 50%, #fbc2eb 100%);
    border: none;
    transition: all 0.3s ease;
    margin: 0 auto;
    display: block;
    text-align: center;
    line-height: 50px;
    letter-spacing: 2px;
    font-weight: 500;
    box-shadow: 0 5px 15px rgba(251, 194, 235, 0.5);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(251, 194, 235, 0.7);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 5px 15px rgba(107, 115, 255, 0.2);
    }
  }
}

.login-compliance-tip {
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  color: #7a7a7a;
  line-height: 1.6;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 响应式调整
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    height: auto;
    width: 100%;
    max-width: 500px;
  }

  .decoration-area {
    padding: 30px;
    min-height: 300px;
  }
  
  .scratch-cat-container {
    width: 180px;
    height: 180px;
    left: 10px;
  }

  .login-area {
    padding: 30px;
  }

  .login-form-container {
    padding: 0 20px;
  }
}
</style>